import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { Save, Edit2, Download, Users, ArrowUp, ArrowDown, BarChart3, TrendingUp, Search, X, AlertTriangle, RefreshCw } from "lucide-react";
import html2pdf from "html2pdf.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => {
  if (v == null || isNaN(v)) return "";
  return new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(v);
};

const fmtPct = (v) => {
  if (v == null || isNaN(v)) return "";
  return `${v.toFixed(1)}%`;
};

const MONTHS = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

const BUDGET_METRICS = [
  { key: 'revenue', label: 'Ricavi' },
  { key: 'variable_costs', label: 'Costi Variabili' },
  { key: 'fixed_costs', label: 'Costi Fissi' },
  { key: 'depreciation', label: 'Ammortamenti' },
  { key: 'financial_charges', label: 'Oneri Finanziari' },
  { key: 'taxes', label: 'Imposte' },
];

// Row styling classes
const RS = {
  header: "bg-slate-800 text-white font-bold",
  cost: "bg-white",
  detail: "bg-slate-50/70 text-slate-600",
  subtotal_green: "bg-emerald-50 font-semibold text-emerald-900",
  subtotal_blue: "bg-blue-50 font-semibold text-blue-900",
  subtotal_indigo: "bg-indigo-50 font-semibold text-indigo-900",
  subtotal: "bg-slate-100 font-semibold",
  total: "bg-slate-900 text-white font-bold",
  cost_owner: "bg-violet-50 text-violet-800",
  total_owner: "bg-violet-100 font-bold text-violet-900",
  subtotal_labor: "bg-amber-50 font-semibold text-amber-800",
};

const COST_STYLES = new Set(['cost','detail','cost_owner','subtotal_labor']);

// Variance arrow component
const VarCell = ({ val, isCost }) => {
  if (val == null || isNaN(val)) return <span className="text-slate-300">-</span>;
  if (Math.abs(val) < 1) return <span className="text-slate-400 tabular-nums">{fmt(val)}</span>;
  // Arrow direction = actual movement (up if val > 0, down if val < 0)
  const goesUp = val > 0;
  // Color: for costs, increase is bad (red); for revenue, increase is good (green)
  const isGood = isCost ? val < 0 : val > 0;
  return (
    <span className={`tabular-nums flex items-center justify-end gap-0.5 ${isGood ? "text-emerald-700" : "text-red-600"}`}>
      {goesUp ? <ArrowUp className="h-2.5 w-2.5" /> : <ArrowDown className="h-2.5 w-2.5" />}
      {fmt(val)}
    </span>
  );
};

export const YearlyReportTable = ({ clientId, clientName, initialYear }) => {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(initialYear || new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([2024, 2025, 2026]);
  const [yearInitialized, setYearInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  const [showOwnerCompDialog, setShowOwnerCompDialog] = useState(false);
  const [budgetForm, setBudgetForm] = useState({});
  const [ownerCompForm, setOwnerCompForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [calc1, setCalc1] = useState({ revenue: 0, vc: 0, comp: 0, profit: 0 });
  const [calc2, setCalc2] = useState({ fc: 0, comp: 0, mdcPct: 0, profit: 0 });
  const [compareMode, setCompareMode] = useState('ap'); // 'ap' = anno precedente, 'budget' = budget
  const [drillDown, setDrillDown] = useState(null); // { metric, month, label }
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  const reloadReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/yearly-report`, { params: { year } });
      setData(res.data);
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // Reset state on client change and auto-detect latest year with data
    setData(null);
    setYearInitialized(false);
    const detectYear = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/clients/${clientId}/available-years`, { headers: { Authorization: `Bearer ${token}` } });
        const yrs = res.data.years || [];
        if (yrs.length > 0) {
          const allYears = [...new Set([...yrs, new Date().getFullYear()])].sort((a, b) => b - a);
          setAvailableYears(allYears);
          if (res.data.latest_year) {
            setYear(res.data.latest_year);
            setYearInitialized(true);
          }
        }
      } catch (e) { /* ignore */ }
    };
    detectYear();
  }, [clientId]);

  // If initialYear is provided, keep year locked to it (don't override with latest)
  useEffect(() => {
    if (initialYear) {
      setYear(initialYear);
      setYearInitialized(true);
    }
  }, [initialYear]);

  useEffect(() => { 
    let cancelled = false;
    const doFetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/clients/${clientId}/yearly-report`, { params: { year } });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error("Errore nel caricamento");
      } finally { if (!cancelled) setLoading(false); }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [clientId, year]);

  const openBudgetDialog = async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/budget`, { params: { year } });
      setBudgetForm(res.data.data || {});
    } catch { setBudgetForm({}); }
    // Pre-fill calculators with actual data averages
    if (data && data.totals) {
      const loadedMonths = data.months?.filter(m => m.has_data).length || 1;
      const avgRev = Math.round((data.totals.revenue || 0) / loadedMonths);
      const avgVC = Math.round((data.totals.variable_costs || 0) / loadedMonths);
      const avgFC = Math.round((data.totals.fixed_costs || 0) / loadedMonths);
      const mdcPct = data.totals.revenue > 0 ? ((data.totals.revenue - data.totals.variable_costs) / data.totals.revenue * 100) : 0;
      // Fetch owner compensation
      let comp = 0;
      try {
        const compRes = await axios.get(`${API}/clients/${clientId}/owner-compensation`, { params: { year } });
        const compData = compRes.data.data || {};
        const compVals = Object.values(compData).filter(v => v > 0);
        comp = compVals.length > 0 ? Math.round(compVals.reduce((a, b) => a + b, 0) / compVals.length) : 0;
      } catch {}
      setCalc1({ revenue: avgRev, vc: avgVC, comp, profit: 0 });
      setCalc2({ fc: avgFC, comp, mdcPct: Math.round(mdcPct * 10) / 10, profit: 0 });
    }
    setShowBudgetDialog(true);
  };

  const openOwnerCompDialog = async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/owner-compensation`, { params: { year } });
      setOwnerCompForm(res.data.data || {});
    } catch { setOwnerCompForm({}); }
    setShowOwnerCompDialog(true);
  };

  const saveBudget = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/budget`, { year, data: budgetForm });
      toast.success("Budget salvato");
      setShowBudgetDialog(false);
      reloadReport();
    } catch { toast.error("Errore"); }
    finally { setSaving(false); }
  };

  const saveOwnerComp = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/owner-compensation`, { year, data: ownerCompForm });
      toast.success("Compenso soci salvato");
      setShowOwnerCompDialog(false);
      reloadReport();
    } catch { toast.error("Errore"); }
    finally { setSaving(false); }
  };

  const openDrillDown = async (metric, month, label) => {
    setDrillDown({ metric, month, label });
    setDrillDownLoading(true);
    setDrillDownData(null);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/drill-down`, {
        params: { year, metric, month: month || 0 }
      });
      setDrillDownData(res.data);
    } catch {
      toast.error("Errore nel caricamento dettaglio");
    }
    setDrillDownLoading(false);
  };

  const closeDrillDown = () => {
    setDrillDown(null);
    setDrillDownData(null);
  };

  const recalculateYear = async () => {
    if (!window.confirm(`Ricalcolare tutti i periodi del ${year}?\n\nQuesta operazione rigenera l'analisi CE di ogni mese usando le mappature attuali. Utile quando vengono rilasciati aggiornamenti al motore di classificazione.`)) return;
    setRecalculating(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/clients/${clientId}/recalculate`, null, {
        params: { year },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data?.message || "Ricalcolo completato");
      await reloadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il ricalcolo");
    } finally {
      setRecalculating(false);
    }
  };

  const exportPDF = () => {
    if (!data) return;
    
    const { row_definitions, months: allMonths, totals: _totals, totals_pct: _totals_pct, prev_year_totals: _prev_year_totals, budget_totals: _budget_totals } = data;
    const _activeMonths = allMonths.filter(m => m.has_data || m.has_prev_year_data);
    if (!_activeMonths.length) return;
    
    const halfPoint = Math.ceil(_activeMonths.length / 2);
    const firstMonths = _activeMonths.slice(0, halfPoint);
    const secondMonths = _activeMonths.slice(halfPoint);
    
    const fmtV = (v) => {
      if (v == null || isNaN(v)) return '';
      return new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(v);
    };
    const fmtP = (v) => {
      if (v == null || isNaN(v)) return '';
      return `${v.toFixed(1)}%`;
    };
    
    const buildPageHTML = (monthSlice, pageLabel) => {
      const isAP = compareMode === 'ap';
      const compLabel = isAP ? 'AP' : 'Bdg';
      const diffLabel = isAP ? 'Sc.' : 'ΔB';
      
      // Elegant header with clean branding
      let html = `
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6mm;padding:4mm 0;border-bottom:2.5px solid #1e293b;">
          <div>
            <div style="font-size:16px;font-weight:800;color:#1e293b;letter-spacing:0.3px;font-family:Arial,Helvetica,sans-serif;">SC Dottori Commercialisti</div>
            <div style="font-size:9px;color:#64748b;margin-top:2px;text-transform:uppercase;letter-spacing:1px;">Controllo di gestione</div>
          </div>
          <div style="text-align:right;">
            <div style="font-size:14px;font-weight:700;color:#1e293b;font-family:Arial,Helvetica,sans-serif;">${clientName || ''}</div>
            <div style="font-size:9px;color:#64748b;margin-top:2px;">CE Riclassificato ${year} &mdash; ${pageLabel}</div>
          </div>
        </div>`;
      
      // Table with improved spacing
      html += `<table style="width:100%;border-collapse:collapse;font-size:9.5px;font-family:Arial,Helvetica,sans-serif;white-space:nowrap;line-height:1.4;">`;
      
      // Thead - month group headers
      html += `<thead>`;
      html += `<tr style="background:#1e293b;">`;
      html += `<th rowspan="2" style="text-align:left;padding:5px 8px;min-width:150px;border-right:1.5px solid #475569;font-size:9px;font-weight:700;color:#fff;vertical-align:bottom;">VOCE</th>`;
      monthSlice.forEach(m => {
        html += `<th colspan="4" style="text-align:center;padding:5px 2px;border-right:1.5px solid #475569;font-size:9.5px;font-weight:700;color:${m.has_data ? '#fff' : '#94a3b8'};">${m.month_name}</th>`;
      });
      html += `<th colspan="4" style="text-align:center;padding:5px 2px;font-size:9.5px;font-weight:700;color:#fff;background:#334155;">Totale ${year}</th>`;
      html += `</tr>`;
      
      // Sub-column headers
      html += `<tr style="background:#334155;border-bottom:2px solid #1e293b;">`;
      for (let i = 0; i <= monthSlice.length; i++) {
        const isTot = i === monthSlice.length;
        const bg = isTot ? 'background:#475569;' : '';
        html += `<th style="text-align:right;padding:3px 5px;${bg}font-size:7.5px;color:#cbd5e1;font-weight:600;text-transform:uppercase;letter-spacing:0.3px;">Val.</th>`;
        html += `<th style="text-align:right;padding:3px 5px;${bg}font-size:7.5px;color:#cbd5e1;font-weight:600;text-transform:uppercase;letter-spacing:0.3px;">%</th>`;
        html += `<th style="text-align:right;padding:3px 5px;${bg}font-size:7.5px;color:#cbd5e1;font-weight:600;text-transform:uppercase;letter-spacing:0.3px;">${compLabel}</th>`;
        html += `<th style="text-align:right;padding:3px 5px;border-right:1.5px solid ${isTot ? '#334155' : '#475569'};${bg}font-size:7.5px;color:#cbd5e1;font-weight:600;text-transform:uppercase;letter-spacing:0.3px;">${diffLabel}</th>`;
      }
      html += `</tr></thead><tbody>`;
      
      // Row styles
      const rowStyles = {
        header: 'background:#f1f5f9;color:#0f172a;font-weight:700;border-top:2px solid #94a3b8;border-bottom:1px solid #cbd5e1;',
        cost: 'background:#ffffff;border-bottom:1px solid #e2e8f0;',
        detail: 'background:#fafbfc;color:#475569;font-size:8.5px;border-bottom:1px solid #f1f5f9;',
        subtotal_green: 'background:#f0fdf4;font-weight:700;color:#166534;border-top:1.5px solid #86efac;border-bottom:1.5px solid #86efac;',
        subtotal_blue: 'background:#eff6ff;font-weight:700;color:#1e3a5f;border-top:1.5px solid #93c5fd;border-bottom:1.5px solid #93c5fd;',
        subtotal_indigo: 'background:#eef2ff;font-weight:700;color:#312e81;border-top:1.5px solid #a5b4fc;border-bottom:1.5px solid #a5b4fc;',
        subtotal: 'background:#f8fafc;font-weight:600;border-top:1px solid #cbd5e1;border-bottom:1px solid #cbd5e1;',
        total: 'background:#0f172a;color:#ffffff;font-weight:700;border-top:2px solid #334155;',
        cost_owner: 'background:#faf5ff;color:#6b21a8;border-bottom:1px solid #e9d5ff;',
        total_owner: 'background:#f3e8ff;font-weight:700;color:#581c87;border-top:1.5px solid #c084fc;border-bottom:1.5px solid #c084fc;',
        subtotal_labor: 'background:#fefce8;font-weight:600;color:#854d0e;border-top:1px solid #fde047;border-bottom:1px solid #fde047;',
      };
      const costStyles = new Set(['cost','detail','cost_owner','subtotal_labor']);
      
      row_definitions.forEach(rowDef => {
        const rs = rowStyles[rowDef.style] || 'border-bottom:1px solid #e2e8f0;';
        const isCost = costStyles.has(rowDef.style);
        const isDetail = rowDef.style === 'detail';
        const isSubtotalOrTotal = rowDef.style?.startsWith('subtotal') || rowDef.style === 'total' || rowDef.style === 'total_owner';
        const key = rowDef.key;
        
        html += `<tr style="${rs}">`;
        html += `<td style="padding:${isDetail ? '3px 8px 3px 20px' : '4px 8px'};border-right:1.5px solid #cbd5e1;${isSubtotalOrTotal ? 'font-weight:700;' : ''}">${rowDef.label}</td>`;
        
        // Month cells
        monthSlice.forEach(m => {
          let val = null, pct = null, comp = null, diff = null;
          if (m.has_data) {
            val = m.values?.[key];
            if (val != null && isCost && val > 0) val = -val;
            pct = m.pct?.[key];
          }
          if (isAP && m.has_prev_year_data) {
            comp = m.prev_year_values?.[key];
            if (comp != null && isCost && comp > 0) comp = -comp;
            diff = m.diff_yoy?.[key];
            if (diff != null && isCost) diff = -diff;
          } else if (!isAP) {
            comp = m.budget?.[key];
            if (comp != null && isCost && comp > 0) comp = -comp;
            diff = m.diff_budget?.[key];
            if (diff != null && isCost) diff = -diff;
          }
          
          const diffColor = diff != null && !isNaN(diff) ? (((isCost ? -diff : diff) > 0) ? 'color:#166534;' : (Math.abs(diff) > 0 ? 'color:#dc2626;' : '')) : '';
          const cellPad = isDetail ? '3px 5px' : '4px 5px';
          
          html += `<td style="text-align:right;padding:${cellPad};">${fmtV(val)}</td>`;
          html += `<td style="text-align:right;padding:${cellPad};color:#94a3b8;">${fmtP(pct)}</td>`;
          html += `<td style="text-align:right;padding:${cellPad};color:#94a3b8;">${fmtV(comp)}</td>`;
          html += `<td style="text-align:right;padding:${cellPad};border-right:1.5px solid #e2e8f0;${diffColor}">${fmtV(diff)}</td>`;
        });
        
        // Total column
        let totVal = _totals[key], totPct = _totals_pct[key];
        if (totVal != null && isCost && totVal > 0) totVal = -totVal;
        let totComp = isAP ? _prev_year_totals?.[key] : _budget_totals?.[key];
        if (totComp != null && isCost && totComp > 0) totComp = -totComp;
        let totDiff = totVal != null && totComp != null ? totVal - totComp : null;
        const totDiffColor = totDiff != null && !isNaN(totDiff) ? (((isCost ? -totDiff : totDiff) > 0) ? 'color:#166534;' : (Math.abs(totDiff) > 0 ? 'color:#dc2626;' : '')) : '';
        const totPad = isDetail ? '3px 5px' : '4px 5px';
        
        html += `<td style="text-align:right;padding:${totPad};background:#f8fafc;font-weight:600;">${fmtV(totVal)}</td>`;
        html += `<td style="text-align:right;padding:${totPad};background:#f8fafc;color:#94a3b8;">${fmtP(totPct)}</td>`;
        html += `<td style="text-align:right;padding:${totPad};background:#f8fafc;color:#94a3b8;">${fmtV(totComp)}</td>`;
        html += `<td style="text-align:right;padding:${totPad};background:#f8fafc;${totDiffColor}">${fmtV(totDiff)}</td>`;
        
        html += `</tr>`;
      });
      
      html += `</tbody></table>`;
      
      // Footer
      html += `<div style="display:flex;justify-content:space-between;align-items:center;margin-top:5mm;padding-top:3mm;border-top:1px solid #e2e8f0;">
        <div style="font-size:7px;color:#94a3b8;">SC Dottori Commercialisti &mdash; Controllo di gestione</div>
        <div style="font-size:7px;color:#94a3b8;">Generato il ${new Date().toLocaleDateString('it-IT')}</div>
      </div>`;
      
      return html;
    };
    
    const page1Label = `${firstMonths[0]?.month_name} - ${firstMonths[firstMonths.length-1]?.month_name}`;
    const page2Label = secondMonths.length > 0 ? `${secondMonths[0]?.month_name} - ${secondMonths[secondMonths.length-1]?.month_name}` : '';
    
    const container = document.createElement('div');
    let pagesHTML = `<div style="page-break-after:always;">${buildPageHTML(firstMonths, page1Label)}</div>`;
    if (secondMonths.length > 0) {
      pagesHTML += `<div>${buildPageHTML(secondMonths, page2Label)}</div>`;
    }
    container.innerHTML = pagesHTML;
    document.body.appendChild(container);
    
    html2pdf().set({
      margin: [5, 4, 5, 4],
      filename: `CE_${(clientName || 'Riclassificato').replace(/\s+/g, '_')}_${year}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2.5, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a3', orientation: 'landscape' },
      pagebreak: { mode: ['css'] },
    }).from(container).save().then(() => {
      document.body.removeChild(container);
    });
    
    toast.success("PDF in generazione...");
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Caricamento...</div>;
  if (!data) return null;

  const { months, totals, totals_pct, prev_year_totals, prev_year_totals_pct, budget_totals, row_definitions, has_previous_year, has_budget, has_owner_compensation, has_rimanenze_neutralizzate, business_type } = data;
  const isPersonalCompany = business_type === 'snc_sas' || business_type === 'ditta_individuale';

  if (!months.some(m => m.has_data)) {
    return (
      <Card><CardContent className="py-12 text-center">
        <p className="text-lg font-semibold mb-2">Nessun dato per il {year}</p>
        <p className="text-muted-foreground">Carica i bilanci mensili per vedere il CE riclassificato</p>
      </CardContent></Card>
    );
  }

  // Filter months that have data or prev year data
  const activeMonths = months.filter(m => m.has_data || m.has_prev_year_data);

  const getCellVal = (m, key, isCost) => {
    if (!m.has_data) return null;
    const v = m.values?.[key];
    if (v == null) return null;
    return isCost && v > 0 ? -v : v;
  };

  const getPrevVal = (m, key, isCost) => {
    if (!m.has_prev_year_data) return null;
    const v = m.prev_year_values?.[key];
    if (v == null) return null;
    return isCost && v > 0 ? -v : v;
  };

  return (
    <div className="space-y-3" data-testid="yearly-report">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold" data-testid="yearly-report-title">CE Riclassificato {year}</h3>
          <p className="text-xs text-muted-foreground">
            {months.filter(m => m.has_data).length}/12 mesi caricati
            {has_previous_year && ` · Confronto con ${year - 1}`}
            {has_budget && " · Budget attivo"}
            {has_owner_compensation && " · Compenso soci attivo"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {has_budget && (
            <div className="flex items-center border rounded-md overflow-hidden" data-testid="compare-mode-toggle">
              <button
                className={`px-2.5 py-1 text-[11px] font-medium flex items-center gap-1 transition-colors ${compareMode === 'ap' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
                onClick={() => setCompareMode('ap')}
                data-testid="compare-mode-ap"
              >
                <TrendingUp className="h-3 w-3" />vs AP
              </button>
              <button
                className={`px-2.5 py-1 text-[11px] font-medium flex items-center gap-1 transition-colors ${compareMode === 'budget' ? 'bg-blue-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
                onClick={() => setCompareMode('budget')}
                data-testid="compare-mode-budget"
              >
                <BarChart3 className="h-3 w-3" />vs Budget
              </button>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={recalculateYear} disabled={recalculating} data-testid="recalculate-year-btn" className="border-amber-300 text-amber-700 hover:bg-amber-50">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${recalculating ? 'animate-spin' : ''}`} />
            {recalculating ? "Ricalcolo..." : "Ricalcola anno"}
          </Button>
          <Button variant="outline" size="sm" onClick={exportPDF} data-testid="export-ce-pdf-btn"><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
          <Button variant="outline" size="sm" onClick={openBudgetDialog} data-testid="edit-budget-btn"><Edit2 className="h-3.5 w-3.5 mr-1" />Budget</Button>
          <Button variant="outline" size="sm" onClick={openOwnerCompDialog} data-testid="edit-owner-comp-btn" className="border-violet-300 text-violet-700">
            <Users className="h-3.5 w-3.5 mr-1" />Compenso Soci
          </Button>
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-20 h-8" data-testid="yearly-report-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {availableYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-[10px] text-muted-foreground items-center bg-slate-50 rounded px-3 py-1.5 border">
        <span><strong>Val.</strong> = Valore mese</span>
        <span><strong>%</strong> = % sul fatturato</span>
        {compareMode === 'ap' ? (
          <>
            <span><strong>AP</strong> = Anno Precedente ({year - 1})</span>
            <span className="flex items-center gap-0.5"><ArrowUp className="h-2.5 w-2.5 text-emerald-600" /><ArrowDown className="h-2.5 w-2.5 text-red-500" /> <strong>Sc.</strong> = Scostamento vs AP</span>
          </>
        ) : (
          <>
            <span className="text-blue-600"><strong>Bdg</strong> = Budget</span>
            <span className="flex items-center gap-0.5 text-blue-600"><ArrowUp className="h-2.5 w-2.5 text-emerald-600" /><ArrowDown className="h-2.5 w-2.5 text-red-500" /> <strong>ΔB</strong> = Scostamento vs Budget</span>
          </>
        )}
        <span className="flex items-center gap-0.5"><Search className="h-2.5 w-2.5 text-blue-500" /> Clicca su un valore per il dettaglio voci</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg shadow-sm" data-testid="yearly-report-table">
        <table className="w-full text-[11px] border-collapse whitespace-nowrap">
          <thead>
            {/* Month group headers */}
            <tr className="bg-slate-200 border-b border-slate-300">
              <th rowSpan={2} className="sticky left-0 z-20 bg-slate-200 text-left px-2 py-1.5 min-w-[180px] border-r-2 border-slate-400 text-[10px] font-bold uppercase tracking-wide align-bottom">
                Voce
              </th>
              {activeMonths.map((m) => (
                <th key={m.month} colSpan={4} className={`text-center px-0 py-1 border-r-2 border-slate-300 text-[10px] font-bold ${m.has_data ? '' : 'text-slate-400'}`}>
                  {m.month_name}
                </th>
              ))}
              <th colSpan={4} className="text-center px-0 py-1 bg-slate-300 text-[10px] font-bold uppercase">
                Totale {year}
              </th>
            </tr>
            {/* Sub-column headers */}
            <tr className="bg-slate-100 border-b-2 border-slate-400">
              {activeMonths.map((m) => (
                <SubHeaders key={m.month} mode={compareMode} />
              ))}
              {/* Total sub-headers */}
              <th className="text-right px-1 py-1 min-w-[62px] border-r border-slate-200 bg-slate-200 text-[9px] font-semibold">Val.</th>
              <th className="text-right px-1 py-1 min-w-[38px] border-r border-slate-200 bg-slate-200 text-[9px] font-semibold">%</th>
              <th className={`text-right px-1 py-1 min-w-[62px] border-r border-slate-200 bg-slate-200 text-[9px] font-semibold ${compareMode === 'budget' ? 'text-blue-600' : ''}`}>
                {compareMode === 'ap' ? 'AP' : 'Bdg'}
              </th>
              <th className={`text-right px-1 py-1 min-w-[62px] border-r-2 border-slate-300 bg-slate-200 text-[9px] font-semibold ${compareMode === 'budget' ? 'text-blue-600' : ''}`}>
                {compareMode === 'ap' ? 'Sc.' : 'ΔB'}
              </th>
            </tr>
          </thead>
          <tbody>
            {row_definitions.map((rowDef) => {
              const rs = RS[rowDef.style] || "";
              const isDetail = rowDef.style === 'detail';
              const isCost = COST_STYLES.has(rowDef.style);
              const isTotal = rowDef.style === 'total';

              return (
                <tr key={rowDef.key} className={`${rs} border-b border-slate-100 hover:brightness-[0.97] transition-all`} data-testid={`row-${rowDef.key}`}>
                  {/* Label */}
                  <td className={`sticky left-0 z-10 px-2 py-1 border-r-2 border-slate-300 text-[11px] ${rs} ${isDetail ? "pl-5 text-[10px]" : ""}`}>
                    {rowDef.label}
                  </td>

                  {/* Month cells: Value | % | Comparison | Variance */}
                  {activeMonths.map((m) => {
                    const curVal = getCellVal(m, rowDef.key, isCost);
                    const pctVal = m.pct?.[rowDef.key];
                    
                    let compVal, diffVal, hasComp;
                    if (compareMode === 'budget') {
                      const bv = m.budget?.[rowDef.key];
                      compVal = bv != null ? (isCost && bv > 0 ? -bv : bv) : null;
                      diffVal = m.diff_budget?.[rowDef.key];
                      hasComp = bv != null;
                    } else {
                      compVal = getPrevVal(m, rowDef.key, isCost);
                      diffVal = m.diff_yoy?.[rowDef.key];
                      hasComp = m.has_prev_year_data;
                    }

                    return (
                      <MonthCells
                        key={m.month}
                        curVal={curVal}
                        pctVal={rowDef.key === 'revenue' ? null : pctVal}
                        prevVal={compVal}
                        diffYoy={diffVal}
                        isCost={isCost}
                        hasData={m.has_data}
                        hasPrev={hasComp}
                        isBudgetMode={compareMode === 'budget'}
                        onDrillDown={() => openDrillDown(rowDef.key, m.month, `${rowDef.label} — ${m.month_name} ${year}`)}
                      />
                    );
                  })}

                  {/* Total cells */}
                  <TotalCells
                    rowKey={rowDef.key}
                    isCost={isCost}
                    totals={totals}
                    totalsPct={totals_pct}
                    prevTotals={compareMode === 'budget' ? (budget_totals || {}) : prev_year_totals}
                    prevTotalsPct={prev_year_totals_pct}
                    isRevenue={rowDef.key === 'revenue'}
                    isBudgetMode={compareMode === 'budget'}
                    onDrillDown={() => openDrillDown(rowDef.key, 0, `${rowDef.label} — Totale ${year}`)}
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Info badges */}
      {has_owner_compensation && (
        <div className="flex items-center gap-2 text-xs text-violet-800 bg-violet-50/50 rounded-lg px-3 py-2 border border-violet-200">
          <Users className="h-4 w-4" />
          <span className="font-semibold">Compenso soci attivo</span>
          <span className="text-violet-600">— Utile Netto (Civilistico) = utile ante compenso amm. - compenso amministratore</span>
        </div>
      )}

      {has_rimanenze_neutralizzate && (
        <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50/50 rounded-lg px-3 py-2 border border-amber-200" data-testid="rimanenze-neutralizzate-badge">
          <AlertTriangle className="h-4 w-4" />
          <span className="font-semibold">Rimanenze neutralizzate</span>
          <span className="text-amber-600">— Le rimanenze iniziali sono state neutralizzate in attesa del caricamento delle rimanenze finali</span>
        </div>
      )}

      {/* Budget Dialog */}
      <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>Budget {year}</DialogTitle></DialogHeader>

          {/* Auto-calculation tools */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {/* Method 1: Target Fixed Costs */}
            <div className="border rounded-lg p-3 bg-blue-50/50 border-blue-200">
              <h4 className="font-semibold text-sm text-blue-800 mb-2">Metodo 1: Quanto posso spendere di costi fissi?</h4>
              <p className="text-[11px] text-blue-600 mb-2">Con il fatturato e i costi variabili attuali, calcola il costo della struttura massimo per pagare i soci e avere utile.</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <label className="text-slate-600">Fatturato mensile medio</label>
                  <Input type="number" className="h-7 text-xs" data-testid="calc1-revenue"
                    value={calc1.revenue} onChange={e => setCalc1(p => ({...p, revenue: parseFloat(e.target.value) || 0}))} />
                </div>
                <div>
                  <label className="text-slate-600">Costi variabili mensili</label>
                  <Input type="number" className="h-7 text-xs" data-testid="calc1-vc"
                    value={calc1.vc} onChange={e => setCalc1(p => ({...p, vc: parseFloat(e.target.value) || 0}))} />
                </div>
                <div>
                  <label className="text-slate-600">Compenso soci mensile</label>
                  <Input type="number" className="h-7 text-xs" data-testid="calc1-comp"
                    value={calc1.comp} onChange={e => setCalc1(p => ({...p, comp: parseFloat(e.target.value) || 0}))} />
                </div>
                <div>
                  <label className="text-slate-600">Utile desiderato mensile</label>
                  <Input type="number" className="h-7 text-xs" data-testid="calc1-profit"
                    value={calc1.profit} onChange={e => setCalc1(p => ({...p, profit: parseFloat(e.target.value) || 0}))} />
                </div>
              </div>
              <div className="mt-2 p-2 bg-white rounded border text-xs">
                <span className="text-slate-500">MDC: </span><strong>{fmt(calc1.revenue - calc1.vc)}</strong>
                <span className="text-slate-500 ml-2">({calc1.revenue > 0 ? ((calc1.revenue - calc1.vc) / calc1.revenue * 100).toFixed(1) : 0}%)</span>
                <br/>
                <span className="text-slate-500">Costi fissi massimi: </span>
                <strong className={calc1.revenue - calc1.vc - calc1.comp - calc1.profit >= 0 ? "text-emerald-700" : "text-red-600"}>
                  {fmt(calc1.revenue - calc1.vc - calc1.comp - calc1.profit)}
                </strong>
              </div>
              <Button size="sm" className="mt-2 w-full bg-blue-600 hover:bg-blue-700 text-xs" data-testid="apply-calc1-btn"
                onClick={() => {
                  const targetFC = Math.max(0, calc1.revenue - calc1.vc - calc1.comp - calc1.profit);
                  const newForm = {};
                  for (let m = 1; m <= 12; m++) {
                    newForm[m.toString()] = { revenue: calc1.revenue, variable_costs: calc1.vc, fixed_costs: targetFC,
                      depreciation: budgetForm[m.toString()]?.depreciation || 0, financial_charges: budgetForm[m.toString()]?.financial_charges || 0, taxes: budgetForm[m.toString()]?.taxes || 0 };
                  }
                  setBudgetForm(newForm);
                  toast.success(`Budget compilato: CF max = ${fmt(targetFC)}/mese`);
                }}>
                Applica a tutti i mesi
              </Button>
            </div>

            {/* Method 2: Target Revenue */}
            <div className="border rounded-lg p-3 bg-amber-50/50 border-amber-200">
              <h4 className="font-semibold text-sm text-amber-800 mb-2">Metodo 2: Quanto devo fatturare?</h4>
              <p className="text-[11px] text-amber-600 mb-2">Con quei costi fissi (compresi stipendi soci) e quella % di margine, quanto dovrebbe essere il fatturato.</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <label className="text-slate-600">Costi fissi mensili</label>
                  <Input type="number" className="h-7 text-xs" data-testid="calc2-fc"
                    value={calc2.fc} onChange={e => setCalc2(p => ({...p, fc: parseFloat(e.target.value) || 0}))} />
                </div>
                <div>
                  <label className="text-slate-600">Compenso soci mensile</label>
                  <Input type="number" className="h-7 text-xs" data-testid="calc2-comp"
                    value={calc2.comp} onChange={e => setCalc2(p => ({...p, comp: parseFloat(e.target.value) || 0}))} />
                </div>
                <div>
                  <label className="text-slate-600">% Margine contributivo</label>
                  <Input type="number" className="h-7 text-xs" data-testid="calc2-mdc"
                    value={calc2.mdcPct} onChange={e => setCalc2(p => ({...p, mdcPct: parseFloat(e.target.value) || 0}))} />
                </div>
                <div>
                  <label className="text-slate-600">Utile desiderato mensile</label>
                  <Input type="number" className="h-7 text-xs" data-testid="calc2-profit"
                    value={calc2.profit} onChange={e => setCalc2(p => ({...p, profit: parseFloat(e.target.value) || 0}))} />
                </div>
              </div>
              <div className="mt-2 p-2 bg-white rounded border text-xs">
                {calc2.mdcPct > 0 ? (<>
                  <span className="text-slate-500">Fatturato necessario: </span>
                  <strong className="text-amber-700">{fmt((calc2.fc + calc2.comp + calc2.profit) / (calc2.mdcPct / 100))}</strong>
                  <br/>
                  <span className="text-slate-500">Costi variabili: </span>
                  <strong>{fmt((calc2.fc + calc2.comp + calc2.profit) / (calc2.mdcPct / 100) * (1 - calc2.mdcPct / 100))}</strong>
                </>) : <span className="text-slate-400">Inserisci il % MDC per calcolare</span>}
              </div>
              <Button size="sm" className="mt-2 w-full bg-amber-600 hover:bg-amber-700 text-xs" data-testid="apply-calc2-btn"
                disabled={calc2.mdcPct <= 0}
                onClick={() => {
                  const targetRev = (calc2.fc + calc2.comp + calc2.profit) / (calc2.mdcPct / 100);
                  const targetVC = targetRev * (1 - calc2.mdcPct / 100);
                  const newForm = {};
                  for (let m = 1; m <= 12; m++) {
                    newForm[m.toString()] = { revenue: Math.round(targetRev), variable_costs: Math.round(targetVC), fixed_costs: calc2.fc,
                      depreciation: budgetForm[m.toString()]?.depreciation || 0, financial_charges: budgetForm[m.toString()]?.financial_charges || 0, taxes: budgetForm[m.toString()]?.taxes || 0 };
                  }
                  setBudgetForm(newForm);
                  toast.success(`Budget compilato: Fatturato target = ${fmt(Math.round(targetRev))}/mese`);
                }}>
                Applica a tutti i mesi
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead><tr className="border-b bg-slate-50">
                <th className="text-left px-2 py-2 min-w-[130px]">Voce</th>
                {MONTHS.map((m, i) => <th key={i} className="text-center px-1 py-2 min-w-[75px]">{m}</th>)}
              </tr></thead>
              <tbody>
                {BUDGET_METRICS.map(metric => (
                  <tr key={metric.key} className="border-b">
                    <td className="px-2 py-1.5 font-medium text-sm">{metric.label}</td>
                    {MONTHS.map((_, i) => (
                      <td key={i} className="px-0.5 py-1">
                        <Input type="number" className="h-7 text-xs tabular-nums text-right w-full"
                          value={budgetForm[(i+1).toString()]?.[metric.key] ?? ""}
                          onChange={e => setBudgetForm(prev => ({ ...prev, [(i+1).toString()]: { ...(prev[(i+1).toString()] || {}), [metric.key]: parseFloat(e.target.value) || 0 }}))}
                          placeholder="0" data-testid={`budget-${metric.key}-${i+1}`} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBudgetDialog(false)}>Annulla</Button>
            <Button onClick={saveBudget} disabled={saving} data-testid="save-budget-btn"><Save className="h-4 w-4 mr-1" />{saving ? "..." : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Owner Comp Dialog */}
      <Dialog open={showOwnerCompDialog} onOpenChange={setShowOwnerCompDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-violet-600" />Compenso Soci/Titolare {year}</DialogTitle></DialogHeader>
          <div className="bg-violet-50 border border-violet-200 rounded p-3 text-sm text-violet-800 mb-4">
            Inserisci il compenso mensile lordo dei soci o del titolare.
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead><tr className="border-b bg-violet-50">
                <th className="text-left px-2 py-2">Voce</th>
                {MONTHS.map((m, i) => <th key={i} className="text-center px-1 py-2 min-w-[70px]">{m}</th>)}
              </tr></thead>
              <tbody><tr className="border-b">
                <td className="px-2 py-1.5 font-medium text-sm whitespace-nowrap">Compenso Soci</td>
                {MONTHS.map((_, i) => (
                  <td key={i} className="px-0.5 py-1">
                    <Input type="number" className="h-7 text-xs tabular-nums text-right w-full"
                      value={ownerCompForm[(i+1).toString()] ?? ""}
                      onChange={e => setOwnerCompForm(prev => ({ ...prev, [(i+1).toString()]: parseFloat(e.target.value) || 0 }))}
                      placeholder="0" data-testid={`owner-comp-${i+1}`} />
                  </td>
                ))}
              </tr></tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOwnerCompDialog(false)}>Annulla</Button>
            <Button onClick={saveOwnerComp} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="save-owner-comp-btn">
              <Save className="h-4 w-4 mr-1" />{saving ? "..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drill-Down Sheet */}
      <Sheet open={!!drillDown} onOpenChange={(open) => !open && closeDrillDown()}>
        <SheetContent side="right" className="w-[500px] sm:w-[600px] overflow-y-auto" data-testid="drill-down-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-base">
              <Search className="h-4 w-4 text-blue-600" />
              Dettaglio voci contabili
            </SheetTitle>
          </SheetHeader>
          {drillDown && (
            <div className="mt-4 space-y-4">
              <div className="px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-blue-900" data-testid="drill-down-title">{drillDown.label}</p>
                {drillDownData && (
                  <p className="text-xs text-blue-600 mt-0.5">
                    {drillDownData.count} {drillDownData.count === 1 ? 'voce' : 'voci'} · Totale: <strong>{fmt(drillDownData.total)}</strong>
                  </p>
                )}
              </div>

              {drillDownLoading ? (
                <div className="text-center py-8 text-muted-foreground text-sm">Caricamento...</div>
              ) : drillDownData?.entries?.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs" data-testid="drill-down-table">
                    <thead>
                      <tr className="bg-slate-100 border-b">
                        <th className="text-left px-3 py-2 font-semibold text-slate-600">Codice</th>
                        <th className="text-left px-3 py-2 font-semibold text-slate-600">Descrizione</th>
                        <th className="text-right px-3 py-2 font-semibold text-slate-600">Importo</th>
                        {drillDown.month === 0 && <th className="text-right px-3 py-2 font-semibold text-slate-600">Mesi</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {drillDownData.entries.map((entry, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`drill-down-entry-${i}`}>
                          <td className="px-3 py-1.5 font-mono text-slate-500">{entry.account_code}</td>
                          <td className="px-3 py-1.5 text-slate-800">{entry.account_name}</td>
                          <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${entry.amount < 0 ? 'text-red-600' : 'text-slate-900'}`}>
                            {fmt(entry.amount)}
                          </td>
                          {drillDown.month === 0 && (
                            <td className="px-3 py-1.5 text-right text-slate-400">{entry.months_present}/12</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-800 text-white font-semibold">
                        <td className="px-3 py-2" colSpan={2}>Totale</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(drillDownData.total)}</td>
                        {drillDown.month === 0 && <td></td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Nessuna voce contabile per questa categoria.
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

// Sub-header cells for each month group
const SubHeaders = ({ mode }) => (
  <>
    <th className="text-right px-1 py-1 min-w-[58px] border-r border-slate-200 text-[9px] font-medium text-slate-600">Val.</th>
    <th className="text-right px-1 py-1 min-w-[34px] border-r border-slate-200 text-[9px] font-medium text-slate-600">%</th>
    <th className={`text-right px-1 py-1 min-w-[58px] border-r border-slate-200 text-[9px] font-medium ${mode === 'budget' ? 'text-blue-500' : 'text-slate-500'}`}>
      {mode === 'ap' ? 'AP' : 'Bdg'}
    </th>
    <th className={`text-right px-1 py-1 min-w-[58px] border-r-2 border-slate-300 text-[9px] font-medium ${mode === 'budget' ? 'text-blue-500' : 'text-slate-500'}`}>
      {mode === 'ap' ? 'Sc.' : 'ΔB'}
    </th>
  </>
);

// 4 cells for one month
const MonthCells = ({ curVal, pctVal, prevVal, diffYoy, isCost, hasData, hasPrev, isBudgetMode, onDrillDown }) => {
  const dash = <span className="text-slate-400">-</span>;
  return (
    <>
      <td className={`px-1 py-1 text-right border-r border-slate-100 tabular-nums ${onDrillDown && hasData && curVal != null ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
        onClick={() => onDrillDown && hasData && curVal != null && onDrillDown()}>
        {hasData && curVal != null ? <span className="tabular-nums">{fmt(curVal)}</span> : dash}
      </td>
      <td className="px-1 py-1 text-right border-r border-slate-100 tabular-nums text-slate-500">
        {hasData && pctVal != null ? <span>{fmtPct(pctVal)}</span> : dash}
      </td>
      <td className={`px-1 py-1 text-right border-r border-slate-100 tabular-nums ${isBudgetMode ? 'text-blue-400' : 'text-slate-400'}`}>
        {hasPrev && prevVal != null ? <span>{fmt(prevVal)}</span> : dash}
      </td>
      <td className="px-1 py-1 text-right border-r-2 border-slate-200 tabular-nums">
        {hasData && hasPrev && diffYoy != null ? <VarCell val={diffYoy} isCost={isCost} /> : dash}
      </td>
    </>
  );
};

// 4 total cells
const TotalCells = ({ rowKey, isCost, totals, totalsPct, prevTotals, prevTotalsPct, isRevenue, isBudgetMode, onDrillDown }) => {
  const val = totals[rowKey];
  const displayVal = val != null ? (isCost && val > 0 ? -val : val) : null;
  const pct = isRevenue ? null : totalsPct[rowKey];
  const prevVal = prevTotals?.[rowKey];
  const displayPrevVal = prevVal != null ? (isCost && prevVal > 0 ? -prevVal : prevVal) : null;
  // diff from raw values (not inverted) for correct arrow direction
  const diff = val != null && prevVal != null ? val - prevVal : null;

  const dash = <span className="text-slate-300">-</span>;
  return (
    <>
      <td className={`px-1 py-1 text-right border-r border-slate-200 tabular-nums font-semibold bg-slate-50 text-slate-900 ${onDrillDown && displayVal != null ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
        onClick={() => onDrillDown && displayVal != null && onDrillDown()}>
        {displayVal != null ? fmt(displayVal) : dash}
      </td>
      <td className="px-1 py-1 text-right border-r border-slate-200 tabular-nums text-slate-500 bg-slate-50">
        {pct != null ? fmtPct(pct) : dash}
      </td>
      <td className={`px-1 py-1 text-right border-r border-slate-200 tabular-nums bg-slate-50 ${isBudgetMode ? 'text-blue-400' : 'text-slate-400'}`}>
        {displayPrevVal != null ? fmt(displayPrevVal) : dash}
      </td>
      <td className="px-1 py-1 text-right border-r-2 border-slate-300 tabular-nums bg-slate-50">
        {diff != null ? <VarCell val={diff} isCost={isCost} /> : dash}
      </td>
    </>
  );
};

export default YearlyReportTable;
